import { useState, useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import logger from '../utils/logger';

/**
 * Hook to dynamically load county-based GeoJSON tiles based on map viewport.
 * Only loads tiles for counties that intersect with the current viewport bounds.
 * Uses a manifest file to efficiently determine which counties to load.
 * 
 * Features:
 * - Only visible at zoom 11+
 * - LEVEL3_SUR labels only at zoom 12+
 * - Viewport culling for performance
 * - Dynamic color based on tileLayer (black in satellite, gray in default)
 * - Immediate cleanup on zoom out
 */
export const useCountyTiles = ({
  manifestPath = '/geodata/tx_survey_polygons_counties/manifest.json',
  basePath = '/geodata/tx_survey_polygons_counties/',
  visible = true,
  minZoom = 11,
  tileLayer = 'default',
  style = {
    fillColor: 'transparent',
    fillOpacity: 0,
    color: '#5a7a7a',
    weight: 1,
    opacity: 0.9
  },
  onEachFeature = null
}) => {
  const map = useMap();
  const [manifest, setManifest] = useState(null);
  const [loadedTiles, setLoadedTiles] = useState(new Map()); // Map of county FIPS -> GeoJSON data
  const [loading] = useState(false);
  const [error, setError] = useState(null);
  
  // Refs for managing layers and labels
  const layersRef = useRef(new Map()); // Store Leaflet layers (fips -> layer)
  const labelMarkersRef = useRef(new Map()); // Store label markers (featureId -> marker)
  const lastBoundsRef = useRef(null);
  const loadingRef = useRef(new Set()); // Track tiles currently being loaded
  const updateTimeoutRef = useRef(null);

  // Load manifest file once
  useEffect(() => {
    if (!visible) return;

    const loadManifest = async () => {
      try {
        const response = await fetch(manifestPath);
        if (!response.ok) {
          throw new Error(`Failed to load manifest: ${response.statusText}`);
        }
        const data = await response.json();
        setManifest(data);
      } catch (err) {
        setError(err.message);
        logger.error('Error loading county manifest:', err);
      }
    };

    loadManifest();
  }, [manifestPath, visible]);

  // Check if bounds intersect
  const boundsIntersect = (mapBounds, countyBounds) => {
    const [minLon, minLat, maxLon, maxLat] = countyBounds;
    const mapNorth = mapBounds.getNorth();
    const mapSouth = mapBounds.getSouth();
    const mapEast = mapBounds.getEast();
    const mapWest = mapBounds.getWest();

    return !(
      maxLon < mapWest ||
      minLon > mapEast ||
      maxLat < mapSouth ||
      minLat > mapNorth
    );
  };

  // Check if polygon intersects viewport
  const polygonInViewport = (feature, mapBounds) => {
    try {
      const coords = feature.geometry.coordinates[0];
      if (!coords || coords.length === 0) return false;
      
      let minLon = Infinity, maxLon = -Infinity;
      let minLat = Infinity, maxLat = -Infinity;
      
      for (const coord of coords) {
        const lon = coord[0];
        const lat = coord[1];
        minLon = Math.min(minLon, lon);
        maxLon = Math.max(maxLon, lon);
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
      }
      
      return !(
        maxLon < mapBounds.getWest() ||
        minLon > mapBounds.getEast() ||
        maxLat < mapBounds.getSouth() ||
        minLat > mapBounds.getNorth()
      );
    } catch (e) {
      return true; // If we can't determine, include it
    }
  };

  // Load county tile
  const loadCountyTile = useCallback(async (fips, filename) => {
    if (loadedTiles.has(fips) || loadingRef.current.has(fips)) {
      return;
    }

    loadingRef.current.add(fips);

    try {
      const response = await fetch(`${basePath}${filename}`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      setLoadedTiles(prev => {
        const next = new Map(prev);
        next.set(fips, data);
        return next;
      });
    } catch (err) {
      logger.warn(`Failed to load county tile ${fips} (${filename}):`, err);
    } finally {
      loadingRef.current.delete(fips);
    }
  }, [basePath, loadedTiles]);

  // Get counties that intersect viewport
  const getIntersectingCounties = useCallback((bounds) => {
    if (!manifest) return [];
    const intersecting = [];
    for (const [fips, countyData] of Object.entries(manifest)) {
      if (boundsIntersect(bounds, countyData.bounds)) {
        intersecting.push({ fips, filename: countyData.filename });
      }
    }
    return intersecting;
  }, [manifest]);

  // Clean up all labels - including duplicates and orphaned markers
  const cleanupLabels = useCallback(() => {
    // First, remove all tracked labels
    labelMarkersRef.current.forEach((marker, featureId) => {
      try {
        if (marker && map.hasLayer(marker)) {
          map.removeLayer(marker);
        }
      } catch (e) {
        logger.warn(`Failed to remove tracked label for ${featureId}:`, e);
      }
    });
    labelMarkersRef.current.clear();
    
    // Then, scan the map directly for any survey-level3-label markers (catches duplicates and orphaned)
    map.eachLayer((layer) => {
      if (layer instanceof L.Marker && layer.options && layer.options.icon) {
        const icon = layer.options.icon;
        if (icon && icon.options && icon.options.className === 'survey-level3-label') {
          try {
            if (map.hasLayer(layer)) {
              map.removeLayer(layer);
            }
          } catch (e) {
            logger.warn('Failed to remove stray label during cleanup:', e);
          }
        }
      }
    });
  }, [map]);

  // Clean up all layers and labels
  const cleanupAll = useCallback(() => {
    layersRef.current.forEach(layer => {
      map.removeLayer(layer);
    });
    layersRef.current.clear();
    cleanupLabels();
  }, [map, cleanupLabels]);

  // Create or update layers from loaded tiles
  const renderLayers = useCallback(() => {
    const currentZoom = map.getZoom();
    const mapBounds = map.getBounds();
    
    // If below min zoom or not visible, clean up everything
    if (!visible || currentZoom < minZoom) {
      cleanupAll();
      return;
    }

    // Determine label visibility
    const showLabels = currentZoom >= 12;
    
    // Clean up labels if zoom < 12
    if (!showLabels) {
      cleanupLabels();
    }

    // Get colors based on tileLayer
    const polygonColor = tileLayer === 'satellite' ? '#000000' : style.color;
    const labelColor = tileLayer === 'satellite' ? '#000000' : '#5a7a7a';
    const textShadow = tileLayer === 'satellite' 
      ? '1px 1px 2px rgba(255, 255, 255, 0.9)' 
      : '1px 1px 1px rgba(255, 255, 255, 0.9)';
    const layerStyle = { ...style, color: polygonColor };

    // Process each loaded tile
    loadedTiles.forEach((geoJsonData, fips) => {
      // Filter features to only those in viewport
      const visibleFeatures = geoJsonData.features.filter(f => polygonInViewport(f, mapBounds));
      
      if (visibleFeatures.length === 0) {
        // Remove layer if no visible features
        const existingLayer = layersRef.current.get(fips);
        if (existingLayer) {
          map.removeLayer(existingLayer);
          layersRef.current.delete(fips);
        }
        // Clean up labels for this county
        labelMarkersRef.current.forEach((marker, featureId) => {
          if (featureId.startsWith(`${fips}-`)) {
            map.removeLayer(marker);
            labelMarkersRef.current.delete(featureId);
          }
        });
        return;
      }

      const filteredGeoJSON = {
        ...geoJsonData,
        features: visibleFeatures
      };

      const existingLayer = layersRef.current.get(fips);
      
      if (existingLayer) {
        // Update existing layer - remove and recreate for simplicity
        map.removeLayer(existingLayer);
        
        // Clean up old labels for this county
        labelMarkersRef.current.forEach((marker, featureId) => {
          if (featureId.startsWith(`${fips}-`)) {
            map.removeLayer(marker);
            labelMarkersRef.current.delete(featureId);
          }
        });
      }

      // Create new layer
      const layer = L.geoJSON(filteredGeoJSON, {
        style: layerStyle,
        interactive: false,
        onEachFeature: (feature, featureLayer) => {
          const props = feature.properties || {};
          const abstractN = props.ABSTRACT_N || '';
          const level3 = props.LEVEL3_SUR || '';
          const level2 = props.LEVEL2_BLO || '';
          const featureId = `${fips}-${abstractN}-${level2}-${level3}`;
          feature._featureId = featureId;

          // Create label if zoom >= 12 and has LEVEL3_SUR
          if (level3 && showLabels && map.getZoom() >= 12) {
            const bounds = featureLayer.getBounds();
            const center = bounds.getCenter();
            
            const labelDiv = L.divIcon({
              className: 'survey-level3-label',
              html: `<div style="
                font-size: 8px;
                font-weight: 500;
                color: ${labelColor};
                text-align: center;
                white-space: nowrap;
                pointer-events: none;
                text-shadow: ${textShadow};
              ">${level3}</div>`,
              iconSize: [30, 12],
              iconAnchor: [15, 6]
            });
            
            const labelMarker = L.marker(center, { 
              icon: labelDiv, 
              interactive: false 
            });
            labelMarker.addTo(map);
            labelMarkersRef.current.set(featureId, labelMarker);
          }
        }
      });

      layer.addTo(map);
      layersRef.current.set(fips, layer);
    });

    // Remove layers for counties that are no longer loaded
    layersRef.current.forEach((layer, fips) => {
      if (!loadedTiles.has(fips)) {
        map.removeLayer(layer);
        layersRef.current.delete(fips);
        
        // Clean up labels for this county
        labelMarkersRef.current.forEach((marker, featureId) => {
          if (featureId.startsWith(`${fips}-`)) {
            map.removeLayer(marker);
            labelMarkersRef.current.delete(featureId);
          }
        });
      }
    });
  }, [map, visible, minZoom, tileLayer, style, loadedTiles, cleanupAll, cleanupLabels]);

  // Update tiles when viewport changes
  useEffect(() => {
    if (!manifest) {
      return;
    }

    // Clean up if conditions aren't met, but don't return early - keep event listeners active
    if (!visible || map.getZoom() < minZoom) {
      cleanupAll();
      setLoadedTiles(new Map());
      lastBoundsRef.current = null;
    }

    const updateTiles = async () => {
      // Check conditions inside updateTiles so it can trigger when conditions become true
      if (!visible || !manifest) {
        cleanupAll();
        setLoadedTiles(new Map());
        lastBoundsRef.current = null;
        return;
      }

      const currentZoom = map.getZoom();
      if (currentZoom < minZoom) {
        cleanupAll();
        setLoadedTiles(new Map());
        lastBoundsRef.current = null;
        return;
      }

      const bounds = map.getBounds();
      const zoom = map.getZoom();

      // Check if bounds have changed significantly
      const boundsKey = `${bounds.getNorth().toFixed(3)}-${bounds.getSouth().toFixed(3)}-${bounds.getEast().toFixed(3)}-${bounds.getWest().toFixed(3)}`;
      if (lastBoundsRef.current?.bounds === boundsKey && lastBoundsRef.current?.zoom === zoom) {
        return;
      }
      lastBoundsRef.current = { bounds: boundsKey, zoom };

      // Get counties to load
      const countiesToLoad = getIntersectingCounties(bounds);
      
      // Load counties in batches
      const batchSize = 5;
      for (let i = 0; i < countiesToLoad.length; i += batchSize) {
        const batch = countiesToLoad.slice(i, i + batchSize);
        await Promise.all(batch.map(({ fips, filename }) => loadCountyTile(fips, filename)));
      }

      // Unload counties no longer visible
      const visibleFips = new Set(countiesToLoad.map(c => c.fips));
      setLoadedTiles(prev => {
        const next = new Map();
        prev.forEach((data, fips) => {
          if (visibleFips.has(fips)) {
            next.set(fips, data);
          }
        });
        return next;
      });
    };

    // Debounced update function
    const scheduleUpdate = () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(() => {
        updateTiles();
      }, 150);
    };

    // Immediate update on moveend/zoomend
    const handleImmediateUpdate = () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTiles();
    };

    // Initial update - will check conditions inside
    updateTiles();

    // Always set up event listeners so tiles load when conditions become true (e.g., zoom in or toggle on)
    map.on('move', scheduleUpdate);
    map.on('moveend', handleImmediateUpdate);
    map.on('zoomend', handleImmediateUpdate);

    return () => {
      map.off('move', scheduleUpdate);
      map.off('moveend', handleImmediateUpdate);
      map.off('zoomend', handleImmediateUpdate);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [map, visible, minZoom, manifest, basePath, cleanupAll, getIntersectingCounties, loadCountyTile]);

  // Render layers when tiles or zoom changes
  useEffect(() => {
    renderLayers();
  }, [renderLayers]);

  // Immediate cleanup on zoom out
  useEffect(() => {
    const handleZoom = () => {
      const zoom = map.getZoom();
      
      if (zoom < minZoom) {
        cleanupAll();
        setLoadedTiles(new Map());
      } else if (zoom < 12) {
        cleanupLabels();
      }
    };

    map.on('zoom', handleZoom);
    map.on('zoomend', handleZoom);

    return () => {
      map.off('zoom', handleZoom);
      map.off('zoomend', handleZoom);
    };
  }, [map, minZoom, cleanupAll, cleanupLabels]);

  // Cleanup stray labels when visible becomes false
  useEffect(() => {
    if (!visible) {
      // Force cleanup of all labels when toggled off
      // Also check map directly for any markers we might have missed
      map.eachLayer((layer) => {
        if (layer instanceof L.Marker && layer.options && layer.options.icon) {
          const icon = layer.options.icon;
          if (icon && icon.options && icon.options.className === 'survey-level3-label') {
            try {
              map.removeLayer(layer);
            } catch (e) {
              logger.warn('Failed to remove stray label:', e);
            }
          }
        }
      });
      cleanupLabels();
    }
  }, [visible, map, cleanupLabels]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupAll();
    };
  }, [cleanupAll]);

  return { loadedTiles, loading, error, tileCount: loadedTiles.size };
};
